/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { isArray } from 'lodash';
import { EventChannel, Task } from 'redux-saga';
import { all, call, cancel, delay, fork, put, race, take } from 'redux-saga/effects';

import { getFirebaseAnalytics } from '~/apis/firebase';
import { config, directionalGameLog, gameLogger } from '~/app';
import { setConnected, setCurrentState } from '~/game';
import { setEventLog } from '~/overlay';
import { GameOverview, GameType } from '~/server/src/dtos';
import { Horse, JoinGameRequest, NewGameRequest, Player } from '~/server/src/dtos/controller';
import { Controller, View } from '~/server/src/interfaces';
import Turfmaster from '~/server/src/tmmodel';

import {
  cancelSetupFree,
  createFree,
  createFreeFailed,
  CreateGameParameters,
  joinFree,
  joinFreeFailed,
  JoinGameParameters,
  refreshFreeGames,
} from '../actions';
import { SetupChannelEvent } from '../channelEvent';
import { setLastActiveGameId } from '../reconnectStorage';
import { actions } from '../state';
import { takeUntilPreserving } from '../utils/saga';

function* takeUntilStatePreserving(
  channel: EventChannel<SetupChannelEvent>,
  desiredState: number,
): Generator<
  unknown,
  { matched: SetupChannelEvent; buffered: SetupChannelEvent[] },
  SetupChannelEvent
> {
  // Preserve non-matching events so game lifecycle events (e.g. STARTGAME/STARTROUND)
  // are not lost when they arrive while we're waiting for the join/create response.
  return yield* takeUntilPreserving(channel, (event) => {
    if (event.state === desiredState) return true;
    const eventName = Object.keys(Turfmaster.stateMachine)[event.state] ?? String(event.state);
    directionalGameLog.inbound(eventName, { phase: 'ignored' }, 'controller event (ignored)');
    gameLogger.debug(
      { state: Object.keys(Turfmaster.stateMachine)[event.state], params: event.params },
      'controller => client (ignored while waiting for response)',
    );
    return false;
  });
}

function isGameplayState(state: number): boolean {
  return (
    state === Turfmaster.stateMachine.STARTGAME ||
    state === Turfmaster.stateMachine.STARTROUND ||
    state === Turfmaster.stateMachine.STARTTURN ||
    state === Turfmaster.stateMachine.MERGECARDS ||
    state === Turfmaster.stateMachine.MERGECARDSOPP ||
    state === Turfmaster.stateMachine.ADDBONUSCARDS ||
    state === Turfmaster.stateMachine.ADDBONUSCARDSOPP ||
    state === Turfmaster.stateMachine.SELECTCARDDICE ||
    state === Turfmaster.stateMachine.SELECTMOVE ||
    state === Turfmaster.stateMachine.SELECTMOVEOPP ||
    state === Turfmaster.stateMachine.SELECTDICES ||
    state === Turfmaster.stateMachine.SELECTDICESOPP ||
    state === Turfmaster.stateMachine.ENDTURN ||
    state === Turfmaster.stateMachine.ENDROUND ||
    state === Turfmaster.stateMachine.ENDGAME ||
    state === Turfmaster.stateMachine.SKIPPLAYER
  );
}

function* waitForJoinHandshake(
  channel: EventChannel<SetupChannelEvent>,
  controller: Controller,
): Generator<
  unknown,
  { matched: SetupChannelEvent; buffered: SetupChannelEvent[] },
  SetupChannelEvent
> {
  const buffered: SetupChannelEvent[] = [];
  while (true) {
    const event: SetupChannelEvent = (yield take(channel)) as SetupChannelEvent;
    if (event.state === Turfmaster.stateMachine.JOINGAME) {
      return { matched: event, buffered };
    }
    if (isGameplayState(event.state)) {
      const fallbackPlayerId =
        typeof (controller as unknown as { getPlayerId?: () => number }).getPlayerId === 'function'
          ? (controller as unknown as { getPlayerId: () => number }).getPlayerId()
          : -1;
      if (typeof fallbackPlayerId === 'number' && fallbackPlayerId >= 0) {
        const inferredJoin: SetupChannelEvent = {
          state: Turfmaster.stateMachine.JOINGAME,
          params: {
            playerid: fallbackPlayerId,
            inferredFromGameplayState: event.state,
          },
        };
        return { matched: inferredJoin, buffered: [...buffered, event] };
      }
    }
    buffered.push(event);
  }
}

export function* sagaSetupFreePullGames(
  view: View,
  controller: Controller,
  channel: EventChannel<SetupChannelEvent>,
) {
  try {
    while (true) {
      yield put(actions.setIsRefreshingFreeGames(true));
      try {
        controller.receiveMsg(Turfmaster.stateMachine.GETGAMES, {
          view,
        });
        const { state, params } = yield take(channel);
        if (state === Turfmaster.stateMachine.GETGAMES) {
          directionalGameLog.inbound(
            'GETGAMES',
            { phase: 'response', payloadSummary: { params } },
            'controller event',
          );
          const games = (isArray(params.games) ? params.games : []) as GameOverview[];
          // Include all games: joinable (waiting + free slot) and watchable (e.g. running, public + allowSpectators)
          yield put(actions.setGames(games));
        }
      } finally {
        yield put(actions.setIsRefreshingFreeGames(false));
      }
      // Wait for the next polling tick, but trigger immediately when the user requests a refresh.
      yield race({
        timeout: delay(config.freeGamePullInterval),
        refresh: take(refreshFreeGames.type),
      });
    }
  } finally {
    // Clean up any resources if needed
    gameLogger.debug('Pull games saga cancelled');
  }
}

function* sagaSetupFree(
  view: View,
  controller: Controller,
  channel: EventChannel<SetupChannelEvent>,
) {
  if (!view || !controller || !channel) {
    gameLogger.warn('sagaSetupFree exited because view, controller or channel is not defined');
    return { ok: false, replayEvents: [] };
  }

  gameLogger.info('sagaSetupFree started');

  const analytics = getFirebaseAnalytics();

  let pullGamesTask: Task | undefined;
  let replayEvents: SetupChannelEvent[];

  try {
    // endless loop because their could be 'wrong' messages in the channel because of
    // multiple processes using it or the controller just sending wrong messages in between
    // we therefore have listen until the right message is received and then break the loop
    while (true) {
      // fork a separate process constantly pulling free games to join
      pullGamesTask = yield fork(sagaSetupFreePullGames, view, controller, channel);

      // wait for game creation, join, or cancel (e.g. user navigated away)
      const action: PayloadAction<unknown> = yield take([
        createFree.type,
        joinFree.type,
        cancelSetupFree.type,
      ]);

      // cancel pull games because we received creation / join / cancel
      if (pullGamesTask) yield cancel(pullGamesTask);

      if (action.type === cancelSetupFree.type) {
        console.log('sagaSetupFree: cancelled (user left setup page)');
        return { ok: false, replayEvents: [] };
      }

      // we are in creation mode so send NEWGAME and wait for NEWGAME response
      if (action.type === createFree.type) {
        const gameParams = (action as PayloadAction<CreateGameParameters>).payload;
        const {
          gameName,
          playerCount,
          horseCount,
          horseSelection,
          visibility,
          allowSpectators,
          chatVisibility,
        } = gameParams;
        const horseAmount = playerCount * horseCount;
        const player: Player = {
          horses: horseSelection.map<Horse>(({ horseId }) => ({
            horseid: horseId,
          })),
        };

        // send game creation request to controller
        const newGameRequest: NewGameRequest = {
          gameType: GameType.Free,
          gameName: gameName || 'remote',
          horseamount: horseAmount,
          player,
          computers: [],
          view,
          visibility,
          allowSpectators,
          chatVisibility,
        };

        controller.receiveMsg(Turfmaster.stateMachine.NEWGAME, newGameRequest);
        directionalGameLog.outbound(
          'NEWGAME',
          {
            phase: 'request',
            payloadSummary: {
              gameType: newGameRequest.gameType,
              gameName: newGameRequest.gameName,
              horseamount: newGameRequest.horseamount,
              visibility: newGameRequest.visibility,
              allowSpectators: newGameRequest.allowSpectators,
              chatVisibility: newGameRequest.chatVisibility,
              horseIds: newGameRequest.player?.horses?.map((h) => h.horseid) ?? [],
              computerCount: newGameRequest.computers?.length ?? 0,
            },
          },
          'controller event',
        );

        // wait for newgame response from controller (while preserving early STARTGAME/STARTROUND)
        const newGameResult = (yield* takeUntilStatePreserving(
          channel,
          Turfmaster.stateMachine.NEWGAME,
        )) as { matched: SetupChannelEvent; buffered: SetupChannelEvent[] };
        replayEvents = newGameResult.buffered;
        const { state, params } = newGameResult.matched;
        directionalGameLog.inbound(
          'NEWGAME',
          { phase: 'response', payloadSummary: { params } },
          'controller event',
        );

        yield put(setCurrentState(state));
        if (state === Turfmaster.stateMachine.NEWGAME) {
          if (params.error) {
            yield put(createFreeFailed({ error: params.error }));
            return { ok: false, replayEvents: [] };
          }
          const game = controller.getGame();
          const board = controller.getBoard();
          // Get eventLog from controller (which was synced from params in newgame handler)
          const eventLogOnNewGame = controller.getEventLog();

          directionalGameLog.inbound(
            'NEWGAME',
            { phase: 'response', payloadSummary: { params, hasGame: !!game, hasBoard: !!board } },
            'controller event',
          );

          // make sure game and board are valid
          if (!game || !board) {
            gameLogger.warn(
              'sagaSetup exit create after game creation because game or board is not defined',
            );
            return { ok: false, replayEvents: replayEvents! };
          }

          yield all([
            put(actions.setGameId(params.gameid)),
            put(actions.setGameType(GameType.Free)),
            put(actions.setPlayerId(params.playerid)),
            // Set eventLog in Redux so game creator sees chat messages
            put(setEventLog(eventLogOnNewGame)),
            put(setConnected(true)),
          ]);
          setLastActiveGameId(params.gameid);

          analytics?.logEvent('start_game', {
            connectionType: controller.getType(),
            gameType: GameType.Free,
            playerCount,
            horseCountPerPlayer: horseCount,
          });

          // game creation successful so break the 'endless' loop
          gameLogger.info('game creation successful');
          break;
        }
      }

      // we are in join mode so send JOINGAME and wait for JOINGAME response
      else if (action.type === joinFree.type) {
        const joinParams = (action as PayloadAction<JoinGameParameters>).payload;
        const { gameId, gameType, horseSelection } = joinParams;
        const player: Player = {
          horses: horseSelection.map<Horse>(({ horseId }) => ({
            horseid: horseId,
          })),
        };

        controller.receiveMsg(Turfmaster.stateMachine.JOINGAME, {
          gameid: gameId,
          player,
          view,
        } as JoinGameRequest);
        directionalGameLog.outbound(
          'JOINGAME',
          {
            phase: 'request',
            ids: { gameId },
            payloadSummary: { horseIds: player.horses?.map((h) => h.horseid) ?? [] },
          },
          'controller event',
        );

        const pendingJoinWatchdog: Task = yield fork(function* () {
          yield delay(2000);
        });
        function* waitJoinOnce() {
          return (yield race({
            result: call(waitForJoinHandshake, channel, controller),
            timeout: delay(3000),
          })) as
            | {
                result: { matched: SetupChannelEvent; buffered: SetupChannelEvent[] };
                timeout?: undefined;
              }
            | { result?: undefined; timeout: true };
        }
        let joinRace = (yield* waitJoinOnce()) as
          | {
              result: { matched: SetupChannelEvent; buffered: SetupChannelEvent[] };
              timeout?: undefined;
            }
          | { result?: undefined; timeout: true };
        if (!joinRace.result) {
          const reconnectableController = controller as unknown as {
            waitForConnection?: () => Promise<void>;
          };
          if (typeof reconnectableController.waitForConnection === 'function') {
            try {
              yield call([reconnectableController, reconnectableController.waitForConnection]);
            } catch {
              // best effort only
            }
          }
          controller.receiveMsg(Turfmaster.stateMachine.JOINGAME, {
            gameid: gameId,
            player,
            view,
          } as JoinGameRequest);
          joinRace = (yield* waitJoinOnce()) as
            | {
                result: { matched: SetupChannelEvent; buffered: SetupChannelEvent[] };
                timeout?: undefined;
              }
            | { result?: undefined; timeout: true };
        }

        if (!joinRace.result) {
          yield cancel(pendingJoinWatchdog);
          yield put(joinFreeFailed({ error: 'Join timeout, please try again.' }));
          return { ok: false, replayEvents: [] };
        }
        const joinGameResult = joinRace.result;
        yield cancel(pendingJoinWatchdog);
        replayEvents = joinGameResult.buffered;
        const { state, params } = joinGameResult.matched;
        directionalGameLog.inbound(
          'JOINGAME',
          { phase: 'response', ids: { gameId }, payloadSummary: { params } },
          'controller event',
        );

        yield put(setCurrentState(state));

        if (state === Turfmaster.stateMachine.JOINGAME) {
          const game = controller.getGame();
          const board = controller.getBoard();
          const eventLog = controller.getEventLog();

          directionalGameLog.inbound(
            'JOINGAME',
            {
              phase: 'response',
              ids: { gameId, playerId: params.playerid },
              payloadSummary: { params, hasGame: !!game, hasBoard: !!board },
            },
            'controller event',
          );

          if (params.error) {
            yield put(joinFreeFailed({ error: params.error }));
            return { ok: false, replayEvents: [] };
          }
          // make sure join was successful and game / board are valid
          if (game && board) {
            yield all([
              put(actions.setGameId(gameId)),
              put(actions.setGameType(gameType)),
              put(actions.setPlayerId(params.playerid)),
              put(setEventLog(eventLog)),
              put(setConnected(true)),
            ]);
            setLastActiveGameId(gameId);

            analytics?.logEvent('join_game', {
              connectionType: controller.getType(),
              gameType,
              playerCount: game.players.length,
              horseCountPerPlayer: game.horses.length / game.players.length,
            });

            // join game was successful so break the 'endless' loop
            gameLogger.info('join game successful');
            break;
          }
        }
      }
    }
  } finally {
    // make sure pullGamesTask cancellation is guaranteed even if this saga is interrupted
    if (pullGamesTask) yield cancel(pullGamesTask);
  }

  // Only reached when we broke after successful create or join; error paths return false above
  // make sure game and board are valid
  if (!controller.getGame() || !controller.getBoard()) {
    gameLogger.warn('sagaSetup exit join after game creation because game or board is not defined');
    return { ok: false, replayEvents: [] };
  }

  // everything worked so notify mainSaga that game started successfully
  gameLogger.info('sagaSetupFree finished successfully');
  return { ok: true, replayEvents: replayEvents! };
}

export default sagaSetupFree;
